import React, { useState } from "react";
import { Badge, Field, Input, Textarea } from "../../components/ui";
import HospitalLinkForm from "../../components/HospitalLinks/HospitalLinkForm";
import CompanyLinkForm from "../../components/CompanyLinks/CompanyLinkForm";
import CompanyEditModal from "../../components/CompanyLinks/CompanyEditModal";
import ReservaLinkForm from "../../components/ReservaLinks/ReservaLinkForm";
import { useCompanies } from "../../hooks/useCompanies";
import { CompanyUpdatePayload } from "../../types/company.types";
import { t } from "../../i18n";
import { Section } from "./parts";
import { PdfDropzone } from "./PdfDropzone";
import { RecipientPicker } from "./RecipientPicker";
import { EmailComposerState } from "./useEmailComposer";

/** Passo 1 do e-mail: destinatários, reservas, PDFs e mensagem. */
export const EmailComposeStep: React.FC<{ s: EmailComposerState }> = ({ s }) => {
  const { updateCompany, updating } = useCompanies();
  // Empresa já vinculada, mas sem e-mail: precisa completar antes de selecionar.
  const [completingId, setCompletingId] = useState<number | null>(null);
  const completing = s.companies.find((c) => c.id === completingId);

  const handleCompleteEmail = async (payload: CompanyUpdatePayload) => {
    if (!completing || !payload.email) return;
    const updated = await updateCompany(completing.id, payload);
    if (!updated) return; // erro já mostrado; o modal fica aberto para corrigir
    s.linkCompany(updated);
    setCompletingId(null);
  };

  const recipientError = s.tried && s.missing.recipient ? <p className="text-caption text-danger">{t("sharing.email.noRecipient")}</p> : null;

  return (
    <div className="flex flex-col gap-5">
      <Section title={t("sharing.email.hospitals")}>
        <RecipientPicker
          items={s.hospitals.map((h) => ({ id: h.id, name: h.name, detail: h.email }))}
          checkedIds={s.hospitalIds}
          onToggle={s.toggleHospital}
          emptyText={t("sharing.email.noHospitals")}
          linkLabel={t("sharing.email.linkHospital")}
          renderLinkForm={(done) => (
            <HospitalLinkForm existingHospitals={s.hospitals} submitLabel={t("sharing.email.linkAndSelect")} onLinked={(h) => { s.linkHospital(h); done(); }} />
          )}
        />
      </Section>

      <Section title={t("sharing.email.companies")}>
        <RecipientPicker
          items={s.companies.map((c) => ({ id: c.id, name: c.name, detail: c.email }))}
          checkedIds={s.companyIds}
          onToggle={s.toggleCompany}
          onCompleteEmail={setCompletingId}
          emptyText={t("sharing.email.noCompanies")}
          linkLabel={t("sharing.email.linkCompany")}
          renderLinkForm={(done) => (
            <CompanyLinkForm existingCompanies={s.companies} requireEmail submitLabel={t("sharing.email.linkAndSelect")} onLinked={(c) => { s.linkCompany(c); done(); }} />
          )}
        />
        {recipientError}
      </Section>

      <Section title={<>{t("sharing.email.reservas")} <Badge>{t("sharing.email.optional")}</Badge></>}>
        <RecipientPicker
          items={s.reservas}
          checkedIds={s.reservaIds}
          onToggle={s.toggleReserva}
          emptyText={t("sharing.email.noReservas")}
          linkLabel={t("sharing.email.linkReserva")}
          renderLinkForm={(done) => (
            <ReservaLinkForm existingReservas={s.reservas} submitLabel={t("sharing.email.linkAndSelect")} onLinked={(r) => { s.linkReserva(r); done(); }} />
          )}
        />
      </Section>

      <PdfDropzone files={s.files} onAdd={s.addFiles} onRemove={s.removeFile} error={s.tried && s.missing.files ? t("sharing.email.noFiles") : undefined} />

      <Field label={t("sharing.email.subject")} error={s.tried && s.missing.subject ? t("sharing.email.subjectRequired") : undefined}>
        <Input value={s.subject} onChange={(e) => s.setSubject(e.target.value)} />
      </Field>

      <Field
        label={
          <span className="flex items-center justify-between gap-2">
            {t("sharing.messageLabel")}
            <Badge tone="warning">{t("sharing.editableTemplate")}</Badge>
          </span>
        }
        hint={s.bodyDirty ? undefined : t("sharing.email.reservasAutoHint")}
      >
        <Textarea value={s.body} onChange={(e) => s.setBody(e.target.value)} rows={9} className="resize-y leading-relaxed" />
      </Field>

      {completing && (
        <CompanyEditModal company={completing} saving={updating} onConfirm={handleCompleteEmail} onClose={() => setCompletingId(null)} requireEmail />
      )}
    </div>
  );
};
